import { AppointmentTerm, BookingRestrictions } from "@tellescope/types-models";
import { BookingPageFormDialogContentsSectionForm } from "./types";
import { Dispatch, SetStateAction } from "react";
import { Button, Checkbox, Input, Label, MultiSelect } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { RELATIONSHIP_TYPES } from "@tellescope/constants";
import { useAuth } from "@tellescope/tanstack-query";
import { Plus } from "lucide-react";
import { TemplateConfigRow } from "./TemplateConfigRow";
import { LocationConfigRow } from "./LocationConfigRow";
import { TermConfigRow } from "./TermConfigRow";

export interface InfoSectionProps {
  form: BookingPageFormDialogContentsSectionForm<
    | "title"
    | "hiddenFromPortal"
    | "archivedAt"
    | "includeRelatedContactTypes"
    | "organizationIds"
    | "calendarEventTemplateIds"
    | "locationIds"
    | "requireLocationSelection"
  >;
  terms: AppointmentTerm[];
  setTerms: Dispatch<SetStateAction<AppointmentTerm[] | undefined>>;
  templateRestrictions: Record<string, BookingRestrictions["restrictions"]> | undefined;
  setTemplateRestrictions: Dispatch<SetStateAction<Record<string, BookingRestrictions["restrictions"]> | undefined>>;
}

export const InfoSection = ({
  form,
  terms,
  setTerms,
  templateRestrictions,
  setTemplateRestrictions,
}: InfoSectionProps) => {
  const { t } = useTranslation("calendar");
  const { organizations } = useAuth();

  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <Label required htmlFor="title">
          {t("Title")}
        </Label>
        <Input
          id="title"
          data-testid="booking-page-title-input"
          {...form.getFieldProps("title")}
          placeholder={t("Booking page name")}
        />
      </div>
      <span className="flex items-center gap-2">
        <Checkbox
          id="hiddenFromPortal"
          checked={form.values.hiddenFromPortal}
          onCheckedChange={(checked) => form.setFieldValue("hiddenFromPortal", !!checked)}
        />
        <Label htmlFor="hiddenFromPortal">{t("Hidden from Portal")}</Label>
      </span>
      <span className="flex items-center gap-2">
        <Checkbox
          id="archivedAt"
          checked={form.values.archivedAt}
          onCheckedChange={(checked) => form.setFieldValue("archivedAt", !!checked)}
        />
        <Label htmlFor="archivedAt">{t("Archived")}</Label>
      </span>
      <div className="space-y-2 w-full">
        <MultiSelect
          {...form.getCustomFieldProps("includeRelatedContactTypes")}
          options={RELATIONSHIP_TYPES.map((val) => ({ label: val, value: val }))}
          className="font-light w-full"
          placeholder={t("Select contact types to include as attendees...")}
        />
        <p className="text-sm text-muted-foreground font-light">
          {t("Selected contact types will be invited as attendees to appointments")}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="organizationIds">{t("Organization")}</Label>
        <Select
          value={JSON.stringify(form.values.organizationIds)}
          onValueChange={(val) => form.setFieldValue("organizationIds", JSON.parse(val))}
        >
          <Select.Trigger className="w-full" id="organizationIds">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {organizations?.map((org) => (
              <Select.Item key={org.id} value={JSON.stringify(org.organizationIds ?? [])}>
                {org.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-4">
        <Label required htmlFor="">
          {t("Calendar Event Templates")}
        </Label>
        <div className="space-y-2">
          <p className="text-muted-foreground font-light text-sm">
            {t("When only one type of event is provided, it will be automatically selected in the booking process.")}
          </p>
          {form.values.calendarEventTemplateIds.map((templateId, i) => (
            <TemplateConfigRow
              key={`template-config-row-${i}`}
              templateId={templateId}
              onTemplateChange={(id) =>
                form.setFieldValue("calendarEventTemplateIds", [
                  ...form.values.calendarEventTemplateIds.slice(0, i),
                  id,
                  ...form.values.calendarEventTemplateIds.slice(i + 1),
                ])
              }
              onDelete={() =>
                form.setFieldValue("calendarEventTemplateIds", [
                  ...form.values.calendarEventTemplateIds.slice(0, i),
                  ...form.values.calendarEventTemplateIds.slice(i + 1),
                ])
              }
              restrictions={templateRestrictions?.[templateId] ?? {}}
              onRestrictionsChange={(newVal) =>
                setTemplateRestrictions((prev) => ({
                  ...prev,
                  [templateId]: newVal,
                }))
              }
            />
          ))}
          <Button
            variant="outline"
            className="w-full font-light"
            data-testid="booking-page-add-template"
            onClick={() =>
              form.setFieldValue("calendarEventTemplateIds", [...form.values.calendarEventTemplateIds, ""])
            }
          >
            <Plus />
            {t("Add Template")}
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        <Label required htmlFor="">
          {t("Locations")}
        </Label>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-muted-foreground font-light text-sm">
              {t("When only one location is provided, it will be automatically selected in the booking process.")}
            </p>
            {form.values.locationIds.map((locationId, i) => (
              <LocationConfigRow
                key={`location-config-row-${i}`}
                locationId={locationId}
                onLocationChange={(id) =>
                  form.setFieldValue("locationIds", [
                    ...form.values.locationIds.slice(0, i),
                    id,
                    ...form.values.locationIds.slice(i + 1),
                  ])
                }
                onDelete={() =>
                  form.setFieldValue("locationIds", [
                    ...form.values.locationIds.slice(0, i),
                    ...form.values.locationIds.slice(i + 1),
                  ])
                }
              />
            ))}
            <Button
              variant="outline"
              className="w-full font-light"
              data-testid="booking-page-add-location"
              onClick={() => form.setFieldValue("locationIds", [...form.values.locationIds, ""])}
            >
              <Plus />
              {t("Add Location")}
            </Button>
          </div>
          {form.values.locationIds.filter(Boolean).length > 1 && (
            <span className="flex items-center gap-2">
              <Checkbox
                checked={form.values.requireLocationSelection}
                onCheckedChange={(checked) => form.setFieldValue("requireLocationSelection", !!checked)}
                id="requireLocationSelection"
              />
              <Label htmlFor="requireLocationSelection">{t("Require Location Selection")}</Label>
            </span>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <Label htmlFor="">{t("Appointment Terms")}</Label>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-muted-foreground font-light text-sm">
              {t(
                "Require patients to acknowledge a document (like a cancellation policy) before completing their appointment booking."
              )}
            </p>
            {terms.map((term, i) => (
              <TermConfigRow
                key={`term-config-row-${i}`}
                term={term}
                onTermChange={(term) => setTerms([...terms.slice(0, i), term, ...terms.slice(i + 1)])}
                onDelete={() => setTerms([...terms.slice(0, i), ...terms.slice(i + 1)])}
              />
            ))}
            <Button
              variant="outline"
              className="w-full font-light"
              onClick={() => setTerms([...terms, { title: "", link: "" }])}
            >
              <Plus />
              {t("Add Term")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
